import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import PageTitle from "../../layouts/PageTitle";
import {
  deleteReclamation,
  getReclamationsByRestaurant,
  restoreReclamation,
} from "../../../services/ClientReclamationService";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import "react-datepicker/dist/react-datepicker.css";
import "./ClientReclamations.css";

const PAGE_SIZE = 8;

const CLAIM_TYPE_OPTIONS = [
  { value: "food", labelKey: "claimCategory_food", fallback: "Food", icon: "fa-cutlery" },
  { value: "service", labelKey: "claimCategory_service", fallback: "Service", icon: "fa-handshake-o" },
  { value: "application", labelKey: "claimCategory_application", fallback: "Application", icon: "fa-mobile" },
  { value: "hygiene", labelKey: "claimCategory_hygiene", fallback: "Hygiene", icon: "fa-tint" },
  { value: "staffing", labelKey: "claimCategory_staffing", fallback: "Staffing", icon: "fa-users" },
  { value: "safety", labelKey: "claimCategory_safety", fallback: "Safety", icon: "fa-shield" },
  { value: "lost_object", labelKey: "claimCategory_lost_object", fallback: "Lost object", icon: "fa-search" },
  { value: "supply", labelKey: "claimCategory_supply", fallback: "Supply", icon: "fa-cube" },
  { value: "management", labelKey: "claimCategory_management", fallback: "Management", icon: "fa-briefcase" },
  { value: "other", labelKey: "claimCategory_other", fallback: "Other", icon: "fa-tag" },
];

const KNOWN_TYPE_VALUES = new Set(CLAIM_TYPE_OPTIONS.map((o) => o.value));

const TYPE_ICON_MAP = CLAIM_TYPE_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.icon;
  return acc;
}, {});

const STATUS_OPTIONS = [
  { value: "pending", labelKey: "reclamationStatus_pending", fallback: "Pending" },
  { value: "in_progress", labelKey: "reclamationStatus_in_progress", fallback: "In progress" },
  { value: "resolved", labelKey: "reclamationStatus_resolved", fallback: "Resolved" },
];

const getInitials = (email = "", name = "") => {
  const source = (name && name !== "-" ? name : email || "").trim();
  if (!source) return "?";
  if (source.includes("@")) {
    const local = source.split("@")[0];
    const parts = local.split(/[._\-\s]+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return local.slice(0, 2).toUpperCase();
  }
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
};

const formatDateShort = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const toDateParam = (value) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const extractClientName = (reclamation) => {
  const directName = (reclamation?.clientName || "").trim();
  if (directName) return directName;
  const userFirst = (reclamation?.user?.firstName || "").trim();
  const userLast = (reclamation?.user?.lastName || "").trim();
  const userFullName = `${userFirst} ${userLast}`.trim();
  if (userFullName) return userFullName;
  const rawDescription = (reclamation?.description || "").trim();
  const marker = "Name:";
  const markerIndex = rawDescription.indexOf(marker);
  if (markerIndex === -1) return "-";
  const afterMarker = rawDescription.slice(markerIndex + marker.length);
  const nextSeparator = afterMarker.indexOf("|");
  const parsedName =
    nextSeparator === -1 ? afterMarker.trim() : afterMarker.slice(0, nextSeparator).trim();
  return parsedName || "-";
};

function CrConfirmModal({
  open,
  variant = "restore",
  title,
  message,
  confirmLabel,
  cancelLabel,
  itemLabel,
  busy,
  onConfirm,
  onClose,
  t,
}) {
  if (!open) return null;

  const isDelete = variant === "delete";

  return createPortal(
    <div className="cr-modal-backdrop" onClick={busy ? undefined : onClose}>
      <div
        className={`cr-modal cr-confirm ${isDelete ? "cr-confirm--danger" : "cr-confirm--restore"}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cr-confirm-title"
      >
        <div className="cr-confirm__body">
          <span className="cr-confirm__icon" aria-hidden="true">
            <i className={`fa ${isDelete ? "fa-trash" : "fa-undo"}`} />
          </span>
          <h2 id="cr-confirm-title">{title}</h2>
          <p>{message}</p>
          {itemLabel ? (
            <div className="cr-confirm__item">
              <span className="cr-confirm__item-name">{itemLabel}</span>
            </div>
          ) : null}
        </div>
        <div className="cr-confirm__foot">
          <button
            type="button"
            className="cr-modal__btn cr-modal__btn--ghost"
            onClick={onClose}
            disabled={busy}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`cr-modal__btn ${isDelete ? "cr-modal__btn--danger" : "cr-modal__btn--restore"}`}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? (
              <>
                <span className="cr-spinner cr-spinner--btn" aria-hidden="true" />
                {t("updating") || "Updating"}...
              </>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

const ArchivedClientReclamations = () => {
  const { t } = useTranslation();
  const [reclamations, setReclamations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [busyId, setBusyId] = useState("");
  const [confirm, setConfirm] = useState(null);

  const fetchReclamations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getReclamationsByRestaurant(
        page,
        PAGE_SIZE,
        search,
        typeFilter,
        statusFilter,
        toDateParam(dateFrom),
        toDateParam(dateTo),
        true
      );
      setReclamations(response.reclamations || []);
      setTotalPages(response.totalPages || 1);
      setTotal(response.total || 0);
    } catch (error) {
      toast.error(
        error.message || t("claimsArchivedLoadError") || "Failed to fetch archived claims"
      );
      setReclamations([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, typeFilter, statusFilter, dateFrom, dateTo, t]);

  useEffect(() => {
    fetchReclamations();
  }, [fetchReclamations]);

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates || [null, null];
    setDateFrom(start);
    setDateTo(end);
    setPage(1);
  };

  const getClaimTypes = (reclamation) => {
    if (Array.isArray(reclamation?.types) && reclamation.types.length > 0) {
      return reclamation.types;
    }
    return reclamation?.type ? [reclamation.type] : ["other"];
  };

  const getTypeLabel = (type, customType = "") => {
    if (type === "other" && customType) {
      return customType.trim();
    }
    const option = CLAIM_TYPE_OPTIONS.find((item) => item.value === type);
    return option ? t(option.labelKey) || option.fallback : type || "—";
  };

  const getTypeBadgeClass = (type) => {
    const normalized = String(type || "other")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");
    return KNOWN_TYPE_VALUES.has(normalized) ? normalized : "other";
  };

  const getTypeIcon = (type) => TYPE_ICON_MAP[getTypeBadgeClass(type)] || "fa-tag";

  const renderTypeBadge = (type, customType = "") => {
    const typeClass = getTypeBadgeClass(type);
    const label = getTypeLabel(type, customType);
    return (
      <span className={`cr-type is-${typeClass}`} title={label} key={`${typeClass}-${label}`}>
        <i className={`fa ${getTypeIcon(type)}`} aria-hidden="true" />
        <span>{label}</span>
      </span>
    );
  };

  const renderTypeBadges = (reclamation) => {
    const types = getClaimTypes(reclamation);
    return (
      <div className="cr-type-list">
        {types.map((type) => renderTypeBadge(type, reclamation?.customType))}
      </div>
    );
  };

  const getStatusLabel = (status) => {
    const option = STATUS_OPTIONS.find((item) => item.value === status);
    return option ? t(option.labelKey) || option.fallback : status || "—";
  };

  const getConfirmItemLabel = (reclamation) => {
    if (!reclamation) return "";
    const name = extractClientName(reclamation);
    const displayName =
      name && name !== "-" ? name : t("claimsAnonymous") || "Anonymous";
    const email = (reclamation.email || reclamation.user?.email || "").trim();
    return email ? `${displayName} · ${email}` : displayName;
  };

  const closeConfirm = () => {
    if (busyId) return;
    setConfirm(null);
  };

  const runConfirm = async () => {
    if (!confirm?.item) return;
    const { type, item } = confirm;
    setBusyId(item._id);
    try {
      if (type === "restore") {
        await restoreReclamation(item._id);
        toast.success(t("claimsRestored") || "Claim restored");
      } else {
        await deleteReclamation(item._id);
        toast.success(t("claimsDeleted") || "Claim deleted");
      }
      setConfirm(null);
      await fetchReclamations();
    } catch (error) {
      toast.error(
        error?.message ||
          (type === "restore"
            ? t("claimsRestoreError") || "Could not restore claim"
            : t("claimsDeleteError") || "Could not delete claim")
      );
    } finally {
      setBusyId("");
    }
  };

  const pageNumbers = useMemo(() => {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }, [totalPages]);

  return (
    <>
      <PageTitle
        motherMenu={t("Archived") || "Archived"}
        activeMenu={t("navArchivedClaims") || "Claims"}
      />

      <div className="cr-page">
        <div className="cr-toolbar">
          <div>
            <h3 className="cr-title">{t("navArchivedClaims") || "Archived claims"}</h3>
            <p className="cr-subtitle">
              <span className="cr-subtitle__dot" aria-hidden="true" />
              {t("claimsArchivedSubtitle") ||
                "Restore claims back to the active list or delete them permanently."}
            </p>
          </div>
        </div>

        <div className="cr-card">
          <div className="cr-filters">
            <div className="cr-search">
              <i className="fa fa-search" aria-hidden="true" />
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder={t("claimsSearchPlaceholder") || "Search by email or phone..."}
              />
            </div>

            <div className="cr-select-wrap">
              <i className="fa fa-tags" aria-hidden="true" />
              <select
                className="cr-input"
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">{t("claimsAllTypes") || "All types"}</option>
                {CLAIM_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey) || option.fallback}
                  </option>
                ))}
              </select>
            </div>

            <div className="cr-select-wrap">
              <i className="fa fa-flag" aria-hidden="true" />
              <select
                className="cr-input"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">{t("claimsAllStatus") || "All status"}</option>
                <option value="pending">{t("reclamationStatus_pending") || "Pending"}</option>
                <option value="in_progress">
                  {t("reclamationStatus_in_progress") || "In progress"}
                </option>
                <option value="resolved">{t("reclamationStatus_resolved") || "Resolved"}</option>
              </select>
            </div>

            <div
              className={`cr-date-range${dateFrom || dateTo ? " is-active" : ""}`}
              aria-label={t("claimsDateRange") || "Date range"}
            >
              <i className="fa fa-calendar" aria-hidden="true" />
              <DatePicker
                selected={dateFrom}
                onChange={handleDateRangeChange}
                startDate={dateFrom}
                endDate={dateTo}
                selectsRange
                maxDate={new Date()}
                placeholderText={t("claimsDateRangePlaceholder") || "Date A - Date B"}
                className="cr-date-range__input"
                dateFormat="dd/MM/yyyy"
                isClearable
                showPopperArrow={false}
              />
            </div>

            <button type="button" className="cr-refresh" onClick={fetchReclamations}>
              <i className="fa fa-refresh" aria-hidden="true" />
              {t("refresh") || "Refresh"}
            </button>
          </div>

          {loading ? (
            <div className="cr-loading">
              <span className="cr-spinner" aria-hidden="true" />
              {t("loading") || "Loading"}...
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table cr-table mb-0">
                  <thead>
                    <tr>
                      <th>
                        <span className="cr-th">
                          <i className="fa fa-user" aria-hidden="true" />
                          {t("claimsColClient") || "Client"}
                        </span>
                      </th>
                      <th>
                        <span className="cr-th">
                          <i className="fa fa-phone" aria-hidden="true" />
                          {t("Phone") || "Phone"}
                        </span>
                      </th>
                      <th>
                        <span className="cr-th">
                          <i className="fa fa-tags" aria-hidden="true" />
                          {t("Type") || "Type"}
                        </span>
                      </th>
                      <th>
                        <span className="cr-th">
                          <i className="fa fa-flag" aria-hidden="true" />
                          {t("status") || "Status"}
                        </span>
                      </th>
                      <th>
                        <span className="cr-th">
                          <i className="fa fa-calendar" aria-hidden="true" />
                          {t("Date") || "Date"}
                        </span>
                      </th>
                      <th>
                        <span className="cr-th">
                          <i className="fa fa-clock-o" aria-hidden="true" />
                          {t("claimsArchivedAt") || "Archived on"}
                        </span>
                      </th>
                      <th className="text-end">
                        <span className="cr-th cr-th--end">
                          <i className="fa fa-ellipsis-h" aria-hidden="true" />
                          {t("actions") || "Actions"}
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {reclamations.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="cr-empty">
                          <i className="fa fa-archive" aria-hidden="true" />
                          <span>{t("claimsArchivedEmpty") || "No archived claims"}</span>
                        </td>
                      </tr>
                    ) : (
                      reclamations.map((reclamation) => {
                        const email =
                          reclamation.email || reclamation.user?.email || "—";
                        const phone =
                          reclamation.phone || reclamation.user?.phone || "—";
                        const name = extractClientName(reclamation);
                        const displayClientName =
                          name && name !== "-"
                            ? name
                            : t("claimsAnonymous") || "Anonymous";
                        const status = reclamation.status || "pending";
                        const busy = busyId === reclamation._id;

                        return (
                          <tr key={reclamation._id} className={`cr-row is-${status}`}>
                            <td>
                              <div className="cr-client">
                                <span className="cr-avatar" aria-hidden="true">
                                  {getInitials(email, name)}
                                </span>
                                <div className="cr-client__meta">
                                  <span className="cr-client__name" title={displayClientName}>
                                    {displayClientName}
                                  </span>
                                  <span className="cr-email" title={email}>
                                    {email}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className="cr-phone">
                                <i className="fa fa-phone" aria-hidden="true" />
                                <span>{phone}</span>
                              </span>
                            </td>
                            <td>{renderTypeBadges(reclamation)}</td>
                            <td>
                              <div className={`cr-status-wrap is-${status}`}>
                                <span className="cr-status-dot" aria-hidden="true" />
                                <span className="cr-status-label">{getStatusLabel(status)}</span>
                              </div>
                            </td>
                            <td>
                              <span className="cr-date">
                                <i className="fa fa-calendar-o" aria-hidden="true" />
                                <span>{formatDateShort(reclamation.createdAt)}</span>
                              </span>
                            </td>
                            <td>
                              <span className="cr-date">
                                <i className="fa fa-clock-o" aria-hidden="true" />
                                <span>{formatDateShort(reclamation.archivedAt)}</span>
                              </span>
                            </td>
                            <td className="text-end">
                              <div className="cr-row-actions">
                                <button
                                  type="button"
                                  className="cr-icon-btn cr-icon-btn--restore"
                                  onClick={() =>
                                    setConfirm({ type: "restore", item: reclamation })
                                  }
                                  disabled={busy}
                                  aria-label={t("claimsRestore") || "Restore"}
                                  title={t("claimsRestore") || "Restore"}
                                >
                                  <i className="fa fa-undo" aria-hidden="true" />
                                </button>
                                <button
                                  type="button"
                                  className="cr-icon-btn cr-icon-btn--delete"
                                  onClick={() =>
                                    setConfirm({ type: "delete", item: reclamation })
                                  }
                                  disabled={busy}
                                  aria-label={t("delete") || "Delete"}
                                  title={t("delete") || "Delete"}
                                >
                                  <i className="fa fa-trash" aria-hidden="true" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {total > 0 && (
                <div className="cr-pagination">
                  <span>
                    {t("claimsShowingCount", {
                      shown: reclamations.length,
                      total,
                    }) || `Showing ${reclamations.length} of ${total} reclamations`}
                  </span>
                  <div className="cr-pagination__pages">
                    <button
                      type="button"
                      className="cr-page-btn"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      aria-label={t("Previous") || "Previous"}
                    >
                      <i className="fa fa-chevron-left" aria-hidden="true" />
                    </button>
                    {pageNumbers.map((num) => (
                      <button
                        type="button"
                        key={num}
                        className={`cr-page-btn${page === num ? " is-active" : ""}`}
                        onClick={() => setPage(num)}
                      >
                        {num}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="cr-page-btn"
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      aria-label={t("Next") || "Next"}
                    >
                      <i className="fa fa-chevron-right" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <CrConfirmModal
        open={Boolean(confirm)}
        variant={confirm?.type || "restore"}
        title={
          confirm?.type === "delete"
            ? t("claimsDeleteTitle") || "Delete claim"
            : t("claimsRestoreTitle") || "Restore claim"
        }
        message={
          confirm?.type === "delete"
            ? t("claimsDeleteConfirm") ||
              "This claim and its images will be permanently deleted."
            : t("claimsRestoreConfirm", {
                item: getConfirmItemLabel(confirm?.item),
              }) ||
              `Restore "${getConfirmItemLabel(confirm?.item)}" to the active claims list?`
        }
        confirmLabel={
          confirm?.type === "delete"
            ? t("delete") || "Delete"
            : t("claimsRestore") || "Restore"
        }
        cancelLabel={t("cancel") || "Cancel"}
        itemLabel={getConfirmItemLabel(confirm?.item)}
        busy={Boolean(busyId)}
        onConfirm={runConfirm}
        onClose={closeConfirm}
        t={t}
      />
    </>
  );
};

export default ArchivedClientReclamations;
